import { useState } from 'react'
import { type ExternalToast, toast } from 'sonner'

import {
  fetchAllBrokersMissingPayments,
  type NAVv2Filters,
} from 'components/hooks/useGetBrokersMissingPayments'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { type CSVHeader, downloadCSV, generateCSV } from 'utils/csv_download'
import { type BrokersBrokerAttentionNeededItem } from 'utils/web_api.schemas'

type NeedsAttentionCSVDownloadProps = {
  filters: NAVv2Filters
  setIsActionsMenuOpen: (isOpen: boolean) => void
}

const columnHeaders: CSVHeader<BrokersBrokerAttentionNeededItem>[] = [
  { label: 'NAV Item ID', key: 'id' },
  { label: 'Enrollment ID', key: 'insurance_enrollment_id' },
  { label: 'Member ID', key: 'member_id' },
  { label: 'Employer ID', key: 'employer_id' },
  { label: 'Employer', key: 'employer_name' },
  { label: 'First Name', key: 'employee_first_name' },
  { label: 'Last Name', key: 'employee_last_name' },
  { label: 'Carrier', key: 'carrier_name' },
  { label: 'Plan', key: 'plan_name' },
  { label: 'Status', key: 'status' },
  { label: 'Type', key: 'enrollment_type' },
  { label: 'Reason', key: 'reason' },
  { label: 'Category', key: 'category' },
  { label: 'Submission Method', key: 'submission_method' },
  { label: 'Brokerage', key: 'brokerage_name' },
  { label: 'Amount Paid', key: 'amount_paid' },
  { label: 'Amount Expected', key: 'amount_expected' },
]

const toastSettings: ExternalToast = {
  position: 'top-center',
}

const NeedsAttentionCSVDownload = ({
  filters,
  setIsActionsMenuOpen,
}: NeedsAttentionCSVDownloadProps) => {
  const [isPending, setIsPending] = useState(false)

  if (isPending) {
    return <LoadingSpinnerCentered />
  }

  return (
    <>
      <div
        onClick={async (e) => {
          // Prevent default + stop propagation to keep the menu open until the download is complete.
          e.preventDefault()
          e.stopPropagation()
          if (isPending) {
            return false
          }
          setIsPending(true)
          try {
            const result = await fetchAllBrokersMissingPayments({
              filters,
              pageSize: 50,
            })
            const csv = generateCSV(columnHeaders, result)

            const date = new Date().toISOString()
            downloadCSV(`needs_attention_${date}.csv`, csv)

            toast.success('CSV Download successful!', toastSettings)
          } catch (e) {
            console.error('Error fetching data for download:', e)
            toast.error(
              'Error downloading CSV! Please try again. If the problem persists, please contact support.',
              toastSettings
            )
            return
          } finally {
            setIsActionsMenuOpen(false)
            setIsPending(false)
          }
        }}
      >
        Download CSV
      </div>
    </>
  )
}

export default NeedsAttentionCSVDownload
